import { Component, Input, OnInit } from '@angular/core';
import { IModalLuminaryModel } from '@types-custom/models/ui/modal-luminary-model';
import { DrawerPanelService } from '@ui-core/services/drawer-panel/drawer-panel.service';
import { Icon } from '@types-custom/models/ui/icon-model';
import { ModalService } from '@ui-core/services/modal/modal.service';
import { ModalLuminaryMaintenanceComponent } from '@ui-core/components/modal-luminary-maintenance/modal-luminary-maintenance.component';
import { LuminaryStatusService } from '@shared/services/luminary-status/luminary-status.service';

/*
 ** Modal Luminary
 ** Render a modal that displays information about luminary.
 */

@Component({
  selector: 'citizen-modal-luminary',
  templateUrl: './modal-luminary.component.html',
  styleUrls: ['./modal-luminary.component.scss'],
})
export class ModalLuminaryComponent implements OnInit {
  @Input() properties: IModalLuminaryModel | undefined;
  Icon = Icon;

  constructor(
    private drawerPanelService: DrawerPanelService,
    private modalService: ModalService,
    private luminaryStatusService: LuminaryStatusService
  ) {}

  ngOnInit(): void {
    this.drawerPanelService.collapseDrawer(true);
    this.setInitialInteractionTime();
  }

  openMaintenanceModal(): void {
    this.modalService.addModal({
      component: ModalLuminaryMaintenanceComponent,
      data: {
        properties: {
          icon: Icon.luminary_w,
          alt: 'título modal icono luminaria',
          title: 'Mantenimiento',
          nextMaintenanceDate: this.getMaintenanceDate(),
          nextMaintenanceLabel: 'Próxima revisión',
        },
      },
    });
  }

  getMaintenanceDate(): string {
    if (this.properties?.nextMaintenanceDate) {
      return this.properties.nextMaintenanceDate;
    }
    // TODO: Implementar la lógica para obtener la fecha de mantenimiento
    return '16/02/2026';
  }

  mockHorasUso(associatedTelecellId: number): number {
    const OFFSET = 4_864_861;
  
    const horas = associatedTelecellId - OFFSET;
  
    // Seguridad por si llega un ID raro
    const MIN = 8_760;   // 1 año
    const MAX = 43_800;  // 5 años
  
    return Math.min(Math.max(horas, MIN), MAX);
  }

  toggleStatus(): void {
    if (!this.properties?.assetId) {
      return;
    }

    const current = this.normalizeStatus(this.properties.status);
    const nextStatus = current === 'Activo' ? 'Inactivo' : 'Activo';
    this.properties.status = nextStatus;
    this.properties.lastInteractionAt = this.formatColombiaISO(
      this.getColombiaNow()
    );

    this.luminaryStatusService.updateStatus({
      assetId: this.properties.assetId.toString(),
      status: nextStatus,
      icon: this.getIconForStatus(nextStatus),
    });
  }

  getStatusLabel(): string {
    const current = this.normalizeStatus(this.properties?.status);
    if (current === 'Activo') {
      return 'Encendida';
    }
    if (current === 'Inactivo') {
      return 'Apagada';
    }
    return 'Sin señal';
  }

  getPowerActionLabel(): string {
    const current = this.normalizeStatus(this.properties?.status);
    return current === 'Activo' ? 'Apagar' : 'Encender';
  }

  getLastInteractionTimeLabel(): string {
    const lastInteraction = this.getLastInteractionDate();
    if (!lastInteraction) {
      return '--:--';
    }
    return this.formatColombiaTime(lastInteraction);
  }

  private normalizeStatus(status?: string): string {
    const normalized = (status ?? '').toLowerCase();
    if (normalized === 'activo') {
      return 'Activo';
    }
    if (normalized === 'inactivo') {
      return 'Inactivo';
    }
    return 'unknown';
  }

  private getIconForStatus(status?: string): Icon {
    const normalized = this.normalizeStatus(status);
    if (normalized === 'Activo') {
      return Icon.layer_luminary_on;
    }
    if (normalized === 'Inactivo') {
      return Icon.layer_luminary_off;
    }
    return Icon.layer_luminary_no_signal;
  }

  private setInitialInteractionTime(): void {
    if (!this.properties?.status || this.properties?.lastInteractionAt) {
      return;
    }
    const status = this.normalizeStatus(this.properties.status);
    if (status === 'unknown') {
      return;
    }
    const lastChange = this.getLastScheduledChange(status);
    if (lastChange) {
      this.properties.lastInteractionAt = this.formatColombiaISO(lastChange);
    }
  }

  private getLastInteractionDate(): Date | undefined {
    if (!this.properties?.status) {
      return undefined;
    }
    if (this.properties.lastInteractionAt) {
      return new Date(this.properties.lastInteractionAt);
    }
    const status = this.normalizeStatus(this.properties.status);
    return this.getLastScheduledChange(status);
  }

  private getLastScheduledChange(status: string): Date | undefined {
    if (status === 'unknown') {
      return undefined;
    }
    const now = this.getColombiaNow();
    const hour = now.getHours();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();

    if (status === 'Activo') {
      if (hour >= 17) {
        return new Date(year, month, day, 17, 0, 0);
      }
      // Entre medianoche y 6am
      return new Date(year, month, day - 1, 17, 0, 0);
    }

    // Inactivo
    if (hour >= 6 && hour < 17) {
      return new Date(year, month, day, 6, 0, 0);
    }
    if (hour < 6) {
      return new Date(year, month, day - 1, 6, 0, 0);
    }
    return new Date(year, month, day, 6, 0, 0);
  }

  private getColombiaNow(): Date {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const lookup = parts.reduce<Record<string, string>>((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
    const iso = `${lookup.year}-${lookup.month}-${lookup.day}T${lookup.hour}:${lookup.minute}:${lookup.second}`;
    return new Date(iso);
  }

  private formatColombiaTime(date: Date): string {
    const value = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Bogota',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
    return value.replace(':', '.').toLowerCase();
  }

  private formatColombiaISO(date: Date): string {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const lookup = parts.reduce<Record<string, string>>((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
    return `${lookup.year}-${lookup.month}-${lookup.day}T${lookup.hour}:${lookup.minute}:${lookup.second}`;
  }
  
}

